import React from "react";
import { TrendingUp, Users, Building2, Check, ArrowRight, Briefcase, Lightbulb, GraduationCap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "lifelong-learning",
  title: "Lifelong Learning Growth Strategy",
  description: "We co-design sustainable revenue models and market-aligned portfolio strategies that extend your institution's reach beyond traditional degrees.",
  icon: TrendingUp,
  price: "Strategic Retainer & Project-Based",
  features: ["Market Opportunity Mapping & Analysis", "3-Year Revenue Growth Roadmap", "Portfolio Audit & Program Design", "Alternative Credentialing Strategy"],
  link: "/ServicesPage"
}, {
  id: "learner-experience",
  title: "Learner Experience & Retention Design",
  description: "Transform your student support systems into a cohesive, learner-centered ecosystem that drives engagement, satisfaction, and long-term retention.",
  icon: Users,
  price: "Co-Design Engagement",
  features: ["End-to-End Learner Journey Mapping", "Retention Systems Playbook", "Student Success Framework Design", "Tech Stack Optimization for Support"],
  link: "/ServicesPage"
}, {
  id: "leadership-capability",
  title: "Leadership & Organizational Capability",
  description: "We don't just deliver a plan; we build the internal capacity, governance, and change management structures your team needs to execute it independently.",
  icon: Building2,
  price: "Capacity Building Partnership",
  features: ["Change Management Toolkit & Training", "Internal Governance Structure Design", "Staff Capability Development Workshops", "Operational Efficiency Assessment"],
  link: "/ServicesPage"
}];
export default function OurCoreServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="our-core-services" className="bg-background py-20 lg:py-32 relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-3">
            Our Core Services
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">We don't just consult; we co-build. Our services are designed to embed capability within your institution, ensuring the growth you achieve is growth you own. Our focus is on driving measurable, sustainable results in
three critical areas: enrollment growth, brand relevance,
and operational efficiency, empowering institutions to
compete and succeed in the future of higher education.</p>
        </div>

        <div className="flex flex-col gap-8 max-w-5xl mx-auto">
          {services.map((service, i) => <div key={service.id} data-index={i} className={`transition-all duration-700 delay-[${i * 150}ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <Card className="border-border/60 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden bg-card/50 backdrop-blur-sm">
                <div className="grid md:grid-cols-[1.2fr_1fr] gap-0">
                  
                  {}
                  <div className="p-8 md:p-10 flex flex-col justify-between h-full border-b md:border-b-0 md:border-r border-border/60">
                    <div>
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 text-primary">
                        <service.icon className="w-7 h-7" />
                      </div>
                      
                      <CardTitle className="text-2xl md:text-3xl font-serif text-primary mb-4">
                        {service.title}
                      </CardTitle>
                      
                      <CardDescription className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
                        {service.description}
                      </CardDescription>
                    </div>

                    <div className="mt-auto pt-6 border-t border-border/40">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-primary/80 bg-primary/5 px-3 py-1.5 rounded-full w-fit">
                          <Briefcase className="w-4 h-4" />
                          {service.price}
                        </div>
                        <Button asChild variant="default" className="w-full sm:w-auto group">
                          <Link to={service.link}>
                            Explore Service
                            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="p-8 md:p-10 bg-secondary/5 flex flex-col justify-center h-full">
                    <h4 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-secondary" />
                      What We Build Together
                    </h4>
                    <ul className="space-y-4">
                      {service.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3">
                          <div className="mt-1 min-w-5 min-h-5 rounded-full bg-secondary/10 flex items-center justify-center">
                            <Check className="w-3.5 h-3.5 text-secondary" strokeWidth={3} />
                          </div>
                          <span className="text-foreground/80 text-sm md:text-base font-medium">
                            {feature}
                          </span>
                        </li>)}
                    </ul>
                    
                    <div className="mt-8 pt-6 border-t border-secondary/10">
                      <p className="text-sm text-muted-foreground italic">
                        "Designed to leave you stronger than we found you."
                      </p>
                    </div>
                  </div>

                </div>
              </Card>
            </div>)}
        </div>

        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-muted-foreground mb-6">
            Not sure where to start? Let's discuss your institution's specific needs.
          </p>
          <Button asChild variant="outline" size="lg" className="border-primary/20 hover:bg-primary/5 text-primary">
            <Link to="/ServicesPage">
              View Full Service Catalog
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}